/**
 * ctgSearch engine: in-memory inverted index + filter + score + sort.
 *
 * Entry points:
 *   buildIndex(cars)  -> engine instance (holds the inverted index)
 *   engine.query(req) -> { hits, nbHits, facets, facets_disjunctive }
 *
 * Request shape (produced by index.js from an InstantSearch request):
 *   {
 *     query:          "cr-v",
 *     page:           0,
 *     hitsPerPage:    24,
 *     facetFilters:   [["make:Honda"], ["model:Civic", "model:Accord"]],
 *     numericFilters: ["price>=20000", "price<=40000"],
 *     facets:         ["make", "model", "trim", ...],
 *     sortIndex:      "clawson_search_production_price_asc" | null,
 *   }
 *
 * See normalize.js for FUZZY_FIELDS / EXACT_FIELDS and the tokenization
 * contract.
 */

import { damerauLevenshtein, budgetFor } from "./dlDistance";
import { tokenize, FUZZY_FIELDS, EXACT_FIELDS } from "./normalize";


/* ------------------------------------------------------------------ */
/* Index construction                                                 */
/* ------------------------------------------------------------------ */

function toStringArray(value) {
  // feature_list and features can arrive as arrays, pipe-strings, or
  // nulls. Normalize to an array of strings for tokenization.
  if (value == null) return [];
  if (Array.isArray(value)) return value.map((v) => (v == null ? "" : String(v)));
  const s = String(value);
  if (s.indexOf("|") >= 0) return s.split("|");
  return [s];
}


// Prefix indexing: contribute entries for every prefix of an indexed
// token with length >= PREFIX_MIN. A query that is a prefix of some
// full token then hits via exact budget-0 match, so "civ" filters to
// Civics without the fuzzy scan. Prefix entries carry a fraction of
// the original boost so a car matching the full "civic" token
// outranks one matching only via prefix.
const PREFIX_MIN = 3;
const PREFIX_SCORE_FACTOR = 0.5;


function addPosting(token, carIdx, boost, tokenToPostings, seenTokens, allTokens) {
  if (!seenTokens.has(token)) {
    seenTokens.add(token);
    allTokens.push(token);
  }
  let postings = tokenToPostings.get(token);
  if (!postings) {
    postings = new Map();
    tokenToPostings.set(token, postings);
  }
  const existing = postings.get(carIdx);
  if (existing) {
    existing.score += boost;
    existing.hits += 1;
  } else {
    postings.set(carIdx, { score: boost, hits: 1 });
  }
}


export function buildIndex(cars) {
  // tokenToPostings: token -> Map<carIdx, { score, hits }>
  //   score = sum of field boosts for this token across all fuzzy fields
  //   hits  = count of fields that contained this token (unused for now
  //           but reserved for phrase scoring)
  const tokenToPostings = new Map();
  // Collect all unique tokens into a flat array for fuzzy scan.
  const allTokens = [];
  const seenTokens = new Set();

  // Exact index: field+value -> Set<carIdx>. Used for EXACT_FIELDS.
  const exactIndex = new Map();

  // Pre-sort keys we compute per-car for sort tiebreakers.
  const carMeta = new Array(cars.length);

  for (let idx = 0; idx < cars.length; idx++) {
    const car = cars[idx];

    // Fuzzy fields: tokenize and accumulate boosted scores. Each token
    // also contributes prefix entries of length >= PREFIX_MIN so a
    // user typing "civ" matches cars whose full token is "civic".
    // Prefix hits carry PREFIX_SCORE_FACTOR of the full-token boost so
    // longer, more specific matches still outrank partial ones.
    for (const [field, boost] of Object.entries(FUZZY_FIELDS)) {
      const raw = car[field];
      if (raw == null) continue;
      const pieces = toStringArray(raw);
      for (const piece of pieces) {
        const toks = tokenize(piece);
        for (const t of toks) {
          addPosting(t, idx, boost, tokenToPostings, seenTokens, allTokens);
          // Prefix entries. "civic" at PREFIX_MIN=3 contributes "civ"
          // and "civi" in addition to "civic". The user types "civ"
          // -> exact budget-0 hit on "civ" -> car is returned.
          for (let plen = PREFIX_MIN; plen < t.length; plen++) {
            const prefix = t.substring(0, plen);
            addPosting(
              prefix, idx, boost * PREFIX_SCORE_FACTOR,
              tokenToPostings, seenTokens, allTokens
            );
          }
        }
      }
    }

    // Exact fields (stock_number, vin): indexed TWO ways.
    //
    // 1. exactIndex: field + full normalized value -> car idx. This is
    //    the "I typed the whole thing" fast path -- a complete stock
    //    number resolves to exactly one car with zero fuzzy work.
    //
    // 2. tokenToPostings: full value + every prefix of length >=
    //    PREFIX_MIN. This is the "I'm typing it in" path -- as the
    //    user types H -> H5 -> H55 -> H553 -> H5539 -> H55398, each
    //    partial prefix exact-matches a token in the index as soon
    //    as it reaches length PREFIX_MIN. No fuzzy budget needed.
    //
    // Fuzzy budget stays at 0 for query tokens that exact-match an
    // exact-field value (handled by the exactIndex fast path above).
    // A query token that's close-but-not-exact (typo in a stock
    // number) does NOT fuzzy-match stock numbers -- stock numbers
    // are not in FUZZY_FIELDS, so queryMatches() won't score them.
    // That's intentional: a typo in a stock number should return
    // nothing, not "almost that one."
    for (const field of EXACT_FIELDS) {
      const raw = car[field];
      if (raw == null) continue;
      const normalized = String(raw).toLowerCase().trim();
      if (!normalized) continue;
      // Fast path.
      const key = field + ":" + normalized;
      let set = exactIndex.get(key);
      if (!set) { set = new Set(); exactIndex.set(key, set); }
      set.add(idx);
      // Prefix path. Add the full value and every prefix as a token.
      // No field boost -- prefix matches on exact fields are already
      // narrow, we don't need to re-rank them.
      addPosting(normalized, idx, 1.0, tokenToPostings, seenTokens, allTokens);
      for (let plen = PREFIX_MIN; plen < normalized.length; plen++) {
        addPosting(
          normalized.substring(0, plen), idx, PREFIX_SCORE_FACTOR,
          tokenToPostings, seenTokens, allTokens
        );
      }
    }

    // Sort/tiebreaker metadata.
    carMeta[idx] = {
      showLot: car.showLot ? 1 : 0,
      transit: car.transit ? 1 : 0,
      age: typeof car.age === "number" ? car.age : 0,
      photo_count: typeof car.photo_count === "number" ? car.photo_count : 0,
    };
  }

  return {
    cars: cars,
    carMeta: carMeta,
    tokenToPostings: tokenToPostings,
    allTokens: allTokens,
    exactIndex: exactIndex,
  };
}


/* ------------------------------------------------------------------ */
/* Query: match + score                                               */
/* ------------------------------------------------------------------ */

/**
 * For one normalized query token, find all index tokens within the
 * length-based edit budget. Returns a Map<indexToken, editDistance>.
 */
function matchToken(queryTok, index) {
  const budget = budgetFor(queryTok.length);
  const out = new Map();
  // Fast path: exact hit, no scan.
  if (index.tokenToPostings.has(queryTok)) {
    out.set(queryTok, 0);
  }
  if (budget === 0) return out;
  // Fuzzy scan: iterate the flat token list once. Each DL call bails
  // early when the length-difference or row-min exceeds budget.
  for (const t of index.allTokens) {
    if (t === queryTok) continue; // already handled
    const d = damerauLevenshtein(queryTok, t, budget);
    if (d <= budget) out.set(t, d);
  }
  return out;
}


/**
 * Score and return the carIdxs that match the full query. AND semantics
 * across query tokens: each token must have at least one match. A car's
 * query score is the sum of its best match score per query token.
 *
 * Returns Map<carIdx, textScore>. textScore = 0 when the query is empty
 * (no text constraint).
 */
function queryMatches(queryTokens, index) {
  if (queryTokens.length === 0) {
    // Empty query: every car matches with score 0.
    const all = new Map();
    for (let i = 0; i < index.cars.length; i++) all.set(i, 0);
    return all;
  }

  // Per-token: build Map<carIdx, bestScoreForThisToken>.
  const perToken = [];
  for (const qt of queryTokens) {
    const matches = matchToken(qt, index);
    if (matches.size === 0) {
      // No index token within budget for this query token -> no results.
      return new Map();
    }
    const perCar = new Map();
    for (const [idxTok, dist] of matches.entries()) {
      const postings = index.tokenToPostings.get(idxTok);
      if (!postings) continue;
      // Match quality: full boost for exact, decays with edit distance.
      const quality = 1 - dist / Math.max(qt.length, 1);
      for (const [carIdx, p] of postings.entries()) {
        const contribution = p.score * quality;
        const prev = perCar.get(carIdx);
        if (prev === undefined || contribution > prev) {
          perCar.set(carIdx, contribution);
        }
      }
    }
    perToken.push(perCar);
  }

  // Intersect across tokens. A car must appear in every perToken map.
  // Sum its contributions as the aggregate text score.
  const base = perToken[0];
  const out = new Map();
  outer: for (const [carIdx, firstScore] of base.entries()) {
    let total = firstScore;
    for (let i = 1; i < perToken.length; i++) {
      const s = perToken[i].get(carIdx);
      if (s === undefined) continue outer;
      total += s;
    }
    out.set(carIdx, total);
  }
  return out;
}


/**
 * Exact-only variant: each query token must match an index token
 * verbatim (edit budget = 0). Used by the header autocomplete so a
 * misspelled query returns no suggestions instead of fuzzy guesses.
 * Prefix matching is still allowed via tokenize()'s hyphen-splitting
 * so "civ" matches the "civic" token's prefix via substring... wait,
 * no, we tokenize both sides; exact means exact token identity.
 *
 * If prefix-as-you-type behavior is wanted later for autocomplete,
 * extend this to also match any index token that starts with the
 * query token. For now, exact token equality.
 */
function exactQueryMatches(queryTokens, index) {
  if (queryTokens.length === 0) {
    const all = new Map();
    for (let i = 0; i < index.cars.length; i++) all.set(i, 0);
    return all;
  }
  const perToken = [];
  for (const qt of queryTokens) {
    const postings = index.tokenToPostings.get(qt);
    if (!postings) return new Map();
    const perCar = new Map();
    for (const [carIdx, p] of postings.entries()) {
      perCar.set(carIdx, p.score);
    }
    perToken.push(perCar);
  }
  const base = perToken[0];
  const out = new Map();
  outer: for (const [carIdx, firstScore] of base.entries()) {
    let total = firstScore;
    for (let i = 1; i < perToken.length; i++) {
      const s = perToken[i].get(carIdx);
      if (s === undefined) continue outer;
      total += s;
    }
    out.set(carIdx, total);
  }
  return out;
}


/* ------------------------------------------------------------------ */
/* Filters                                                            */
/* ------------------------------------------------------------------ */

/**
 * facetFilters is an array where each entry is either:
 *   - a string like "make:Honda" (single-value AND)
 *   - an array like ["model:Civic", "model:Accord"] (disjunctive OR
 *     within the group, AND with other groups)
 *
 * Returns true if the car passes all groups.
 *
 * If excludeAttribute is given, filters whose attribute matches it are
 * skipped. This is how disjunctive facet counts work: compute counts
 * for facet X as if X's own filter were off.
 */
function passesFacetFilters(car, facetFilters, excludeAttribute) {
  if (!facetFilters || facetFilters.length === 0) return true;
  for (const group of facetFilters) {
    const arr = Array.isArray(group) ? group : [group];
    if (arr.length === 0) continue;
    // All entries in a group share an attribute by convention.
    const firstColon = arr[0].indexOf(":");
    const attr = firstColon < 0 ? arr[0] : arr[0].slice(0, firstColon);
    if (attr === excludeAttribute) continue;
    let anyMatch = false;
    for (const entry of arr) {
      const i = entry.indexOf(":");
      const value = i < 0 ? "" : entry.slice(i + 1);
      const carValue = car[attr];
      if (carValue == null) continue;
      // The "new" facet is boolean on the car but string on the filter.
      const carStr = String(carValue);
      if (carStr === value) { anyMatch = true; break; }
      // Array-valued fields like feature_list: match if value is in the
      // list.
      if (Array.isArray(carValue) && carValue.map(String).indexOf(value) >= 0) {
        anyMatch = true; break;
      }
      if (typeof carValue === "string" && carValue.indexOf("|") >= 0) {
        if (carValue.split("|").indexOf(value) >= 0) { anyMatch = true; break; }
      }
    }
    if (!anyMatch) return false;
  }
  return true;
}


/**
 * numericFilters is an array of strings like "price>=20000", "year<2020".
 * Supported ops: >=, <=, >, <, =, !=
 */
const NUMFILT_RE = /^([a-zA-Z_][a-zA-Z0-9_]*)\s*(>=|<=|>|<|=|!=)\s*(-?\d+(?:\.\d+)?)$/;

function passesNumericFilters(car, numericFilters) {
  if (!numericFilters || numericFilters.length === 0) return true;
  for (const entry of numericFilters) {
    const m = NUMFILT_RE.exec(String(entry));
    if (!m) continue; // malformed -> ignore (don't block the car)
    const attr = m[1];
    const op = m[2];
    const rhs = parseFloat(m[3]);
    const lhsRaw = car[attr];
    if (lhsRaw == null) return false;
    const lhs = typeof lhsRaw === "number" ? lhsRaw : parseFloat(lhsRaw);
    if (Number.isNaN(lhs)) return false;
    let ok = false;
    switch (op) {
      case ">=": ok = lhs >= rhs; break;
      case "<=": ok = lhs <= rhs; break;
      case ">":  ok = lhs >  rhs; break;
      case "<":  ok = lhs <  rhs; break;
      case "=":  ok = lhs === rhs; break;
      case "!=": ok = lhs !== rhs; break;
    }
    if (!ok) return false;
  }
  return true;
}


/* ------------------------------------------------------------------ */
/* Facet counting                                                     */
/* ------------------------------------------------------------------ */

/**
 * For each facet attribute, count distinct values across the matched
 * set. Returns { make: { Honda: 50, ... }, model: {...}, ... }.
 *
 * If disjunctive=true, each facet attribute's own filter is ignored
 * when computing its counts, so the user still sees the other options
 * in that group after refining it. Non-disjunctive counts honor every
 * active filter.
 */
function countFacets(index, baseMatches, facetFilters, numericFilters, facets, disjunctive) {
  const result = {};
  if (!facets || facets.length === 0) return result;
  for (const attr of facets) {
    const counts = {};
    const exclude = disjunctive ? attr : null;
    for (const [carIdx] of baseMatches.entries()) {
      const car = index.cars[carIdx];
      if (!passesFacetFilters(car, facetFilters, exclude)) continue;
      if (!passesNumericFilters(car, numericFilters)) continue;
      const raw = car[attr];
      if (raw == null) continue;
      // Multi-valued fields contribute to each of their values.
      const values = Array.isArray(raw)
        ? raw
        : (typeof raw === "string" && raw.indexOf("|") >= 0 ? raw.split("|") : [raw]);
      for (const v of values) {
        if (v == null || v === "") continue;
        const key = String(v);
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    result[attr] = counts;
  }
  return result;
}


/* ------------------------------------------------------------------ */
/* Sort                                                               */
/* ------------------------------------------------------------------ */

/**
 * Decode the sort index name chosen by the InstantSearch ais-sort-by
 * dropdown. The hosted index uses names like
 * "clawson_search_production_price_asc"; we accept any suffix of _attr_dir
 * and fall back to relevance-only when no suffix is present.
 *
 * Returns { attr, dir } or null for the default (relevance) index.
 */
function parseSortIndex(sortIndex) {
  if (!sortIndex) return null;
  const m = /_(price|year|mileage)_(asc|desc)$/.exec(sortIndex);
  if (!m) return null;
  return { attr: m[1], dir: m[2] };
}


/**
 * Final sort key: text relevance desc, then showLot asc, transit asc,
 * age desc, photo_count desc. Explicit sort index (price/year/mileage)
 * takes priority over everything but still falls through to the
 * tiebreakers for records with equal primary attributes.
 */
function sortMatches(matched, index, sortSpec) {
  const arr = Array.from(matched.entries()); // [carIdx, textScore]
  const meta = index.carMeta;
  const cars = index.cars;

  arr.sort((a, b) => {
    const [ai, as] = a;
    const [bi, bs] = b;
    const am = meta[ai];
    const bm = meta[bi];

    if (sortSpec) {
      const av = cars[ai][sortSpec.attr];
      const bv = cars[bi][sortSpec.attr];
      const an = typeof av === "number" ? av : parseFloat(av);
      const bn = typeof bv === "number" ? bv : parseFloat(bv);
      const aOk = !Number.isNaN(an);
      const bOk = !Number.isNaN(bn);
      if (aOk && bOk && an !== bn) {
        return sortSpec.dir === "asc" ? an - bn : bn - an;
      }
      if (aOk !== bOk) return aOk ? -1 : 1; // missing values last
    } else {
      if (as !== bs) return bs - as; // relevance desc
    }
    if (am.showLot !== bm.showLot) return am.showLot - bm.showLot;
    if (am.transit !== bm.transit) return am.transit - bm.transit;
    if (am.age !== bm.age) return bm.age - am.age;
    if (am.photo_count !== bm.photo_count) return bm.photo_count - am.photo_count;
    return 0;
  });
  return arr;
}


/* ------------------------------------------------------------------ */
/* Public query                                                       */
/* ------------------------------------------------------------------ */

export function query(index, req) {
  const q = req.query || "";
  const page = req.page || 0;
  const hitsPerPage = req.hitsPerPage || 24;
  const facetFilters = req.facetFilters || [];
  const numericFilters = req.numericFilters || [];
  const facetAttrs = req.facets || [];
  const sortSpec = parseSortIndex(req.sortIndex);
  const exactOnly = !!req.exactOnly;

  // 1. Text match -> Map<carIdx, textScore>
  //
  // Exact-field fast path: if the normalized query is a complete value
  // of some exact field (stock_number, vin), use just those cars and
  // skip the fuzzy scan entirely. Catches the common "I pasted a
  // stock number" case with zero ambiguity.
  const normalized = String(q || "").toLowerCase().trim();
  let textMatches = null;
  if (normalized) {
    for (const field of EXACT_FIELDS) {
      const set = index.exactIndex.get(field + ":" + normalized);
      if (set && set.size > 0) {
        textMatches = new Map();
        for (const carIdx of set) textMatches.set(carIdx, 10);
        break;
      }
    }
  }
  if (textMatches === null) {
    const queryTokens = tokenize(q);
    textMatches = exactOnly
      ? exactQueryMatches(queryTokens, index)
      : queryMatches(queryTokens, index);
  }

  // 2. Apply conjunctive filters to produce the base match set used
  //    for the result listing and non-disjunctive facet counts.
  const filtered = new Map();
  for (const [idx, score] of textMatches.entries()) {
    const car = index.cars[idx];
    if (!passesFacetFilters(car, facetFilters, null)) continue;
    if (!passesNumericFilters(car, numericFilters)) continue;
    filtered.set(idx, score);
  }

  // 3. Sort + paginate.
  const sorted = sortMatches(filtered, index, sortSpec);
  const nbHits = sorted.length;
  const nbPages = Math.max(1, Math.ceil(nbHits / hitsPerPage));
  const startAt = page * hitsPerPage;
  const slice = sorted.slice(startAt, startAt + hitsPerPage);
  const hits = slice.map(([idx]) => index.cars[idx]);

  // 4. Facet counts over the filtered set (already honors facetFilters +
  //    numericFilters passed in). The InstantSearch helper manages
  //    disjunctive faceting by issuing multiple requests -- one per
  //    disjunctive attribute, each with that attribute's own filter
  //    removed. We just honor whatever filters the request supplies.
  const facets = countFacets(index, filtered, facetFilters, numericFilters, facetAttrs, false);

  return {
    hits: hits,
    nbHits: nbHits,
    page: page,
    nbPages: nbPages,
    hitsPerPage: hitsPerPage,
    facets: facets,
    query: q,
  };
}
